import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import OrigineIvaLabel from "@/components/OrigineIvaLabel";
import CurrencyInput, { formatCurrencyIT } from "@/components/CurrencyInput";
import { ShieldCheck, Plus, Pencil, Trash2 } from "lucide-react";
import {
  THEAD_WRAPPER, COL_HEADER_LEFT, COL_HEADER_CENTER,
  COL_HEADER_RIGHT_AMBER, COL_HEADER_RIGHT_BLUE,
  CELL_TEXT_DEFAULT, VALORE_TESTO_DEFAULT,
  CELL_MONEY_ACQUISTO, CELL_MONEY_VENDITA,
  CELL_AZIONI, ICON_BTN_EDIT, ICON_BTN_DELETE,
  COL_WIDTH_ACQUISTO, COL_WIDTH_VENDITA,
} from "@/lib/cardStyles";
import { toast } from "sonner";

/** Colore dedicato Garanzia/Provvigione (Sprint 4F1.1) */
const PROVV_TEXT = "text-emerald-600";
const PROVV_BG = "bg-emerald-100";
const PROVV_BORDER = "border-emerald-200";

const formatEUR = (n) =>
  n === null || n === undefined || n === "" ? "—" : `${formatCurrencyIT(n, false)} €`;
// Sprint FIX-MONITOR2 — Resa UNIFORME campo acquisto mascherato: stringa vuota
const formatEURMasked = (n) =>
  n === null || n === undefined || n === "" ? "" : `${formatCurrencyIT(n, false)} €`;

const DURATA_OPTS = [12, 24, 36, 48, 60];

/** Sprint 4F1.1 — Sezione "Garanzie accessorie" della scheda preventivo/vendita.
 *  Modello: provvigione esplicita riconosciuta dal provider.
 */
export default function GaranzieSection({ vendita, onChange, canEdit, aziendaIvaDefault }) {
  const [modal, setModal] = useState(null);
  const [open, setOpen] = useState(true);
  const arr = vendita?.righe_garanzie || [];
  const providerNames = useProviderNames(arr);

  const totProvvigione = arr.reduce((s, r) => s + (Number(r.provvigione_calcolata) || 0), 0);
  // Sprint FIX-MONITOR2 — se anche una sola riga ha acquisto mascherato (null in
  // Monitor Cliente), il totale acquisto è "mascherato" → non mostrare 0.
  const acquistoMasked = arr.some((r) => r.prezzo_acquisto === null);
  const totAcquisto = acquistoMasked
    ? null
    : arr.reduce((s, r) => s + (Number(r.prezzo_acquisto) || 0), 0);
  const totVendita = arr.reduce((s, r) => s + (Number(r.prezzo_vendita) || 0), 0);

  useEffect(() => { setOpen(arr.length > 0); }, [arr.length]);

  const handleDelete = async (riga) => {
    if (!window.confirm(`Eliminare garanzia "${riga.descrizione}"?`)) return;
    try {
      await api.delete(`/vendite/${vendita.id}/garanzie/${riga.id}`);
      toast.success("Garanzia eliminata");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <Card className="p-3" data-testid="card-garanzie">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 hover:text-emerald-700 transition-colors"
          data-testid="toggle-garanzie"
          data-open={open ? "true" : "false"}
        >
          <span className={`text-slate-400 text-xs transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
          <ShieldCheck className={`h-4 w-4 ${PROVV_TEXT}`} />
          <h3 className="font-heading text-base font-semibold text-slate-900">Garanzie accessorie</h3>
          <span className="text-xs text-slate-400">({arr.length})</span>
          {!open && arr.length > 0 && (
            <span className="text-[11px] text-slate-500 font-mono ml-1">{formatEUR(totVendita)}</span>
          )}
          {totProvvigione > 0 && (
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ml-2 ${PROVV_BG} ${PROVV_TEXT} ${PROVV_BORDER} border`}
              data-testid="garanzie-margine-pill"
            >
              +{formatEUR(totProvvigione)} margine
            </span>
          )}
        </button>
        {canEdit && (
          <Button size="sm" variant="outline"
                  onClick={() => setModal({ editing: null })}
                  data-testid="btn-add-garanzia">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
        )}
      </div>

      {open && (
        arr.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-2 mt-2">Nessuna riga</div>
        ) : (
          <table className="w-full text-sm mt-2" data-testid="garanzie-table">
            <thead className={THEAD_WRAPPER}>
              <tr>
                <th className={COL_HEADER_LEFT}>Provider</th>
                <th className={COL_HEADER_LEFT}>Descrizione</th>
                <th className={`${COL_HEADER_CENTER} w-[70px]`}>Durata</th>
                <th className={`${COL_HEADER_RIGHT_AMBER} ${COL_WIDTH_ACQUISTO}`}>Acquisto</th>
                <th className={`${COL_HEADER_RIGHT_BLUE} ${COL_WIDTH_VENDITA}`}>Vendita</th>
                {canEdit && <th className="w-[70px]" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {arr.map((r) => {
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`garanzia-row-${r.id}`}>
                    <td className={CELL_TEXT_DEFAULT}>
                      <span className={VALORE_TESTO_DEFAULT}>
                        {providerNames[r.provider_id] || <span className="text-slate-400">—</span>}
                      </span>
                    </td>
                    <td className="py-1.5">
                      {r.descrizione}
                      {r.note && <div className="text-[10px] text-slate-400 mt-0.5 italic">{r.note}</div>}
                    </td>
                    <td className="py-1.5 text-center font-mono text-[12px]">{r.durata_mesi}m</td>
                    <td className={CELL_MONEY_ACQUISTO}>
                      {formatEURMasked(r.prezzo_acquisto)}
                    </td>
                    <td className={CELL_MONEY_VENDITA}>
                      {formatEUR(r.prezzo_vendita)}
                    </td>
                    {canEdit && (
                      <td className={CELL_AZIONI}>
                        <button onClick={() => setModal({ editing: r })}
                                className={ICON_BTN_EDIT}
                                title="Modifica"
                                data-testid={`btn-edit-garanzia-${r.id}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(r)}
                                className={ICON_BTN_DELETE}
                                title="Elimina"
                                data-testid={`btn-del-garanzia-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {arr.length > 1 && (
                <tr className="font-semibold text-[12px]">
                  <td className="py-1.5 text-right" colSpan={3}>Totali:</td>
                  <td className="py-1.5 text-right font-mono text-amber-800">{formatEURMasked(totAcquisto)}</td>
                  <td className="py-1.5 text-right font-mono text-blue-800">{formatEUR(totVendita)}</td>
                  {canEdit && <td />}
                </tr>
              )}
            </tbody>
          </table>
        )
      )}

      <GaranziaModal
        open={!!modal}
        onOpenChange={(o) => !o && setModal(null)}
        vendita={vendita}
        editing={modal?.editing}
        onSaved={() => { setModal(null); onChange?.(); }}
        aziendaIvaDefault={aziendaIvaDefault}
      />
    </Card>
  );
}


function useProviderNames(arr) {
  const [map, setMap] = useState({});
  const ids = [...new Set(arr.map((r) => r.provider_id).filter(Boolean))];
  const idsKey = ids.join(",");
  useEffect(() => {
    const missing = ids.filter((id) => !map[id]);
    if (!missing.length) return;
    Promise.all(missing.map((id) =>
      api.get(`/anagrafiche/${id}`).then((r) => r.data).catch(() => null),
    )).then((arrA) => {
      const next = { ...map };
      arrA.forEach((a) => {
        if (!a) return;
        next[a.id] = a.tipo_persona === "azienda"
          ? a.ragione_sociale
          : [a.cognome, a.nome].filter(Boolean).join(" ") || a.id.slice(0, 8);
      });
      setMap(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);
  return map;
}


function GaranziaModal({ open, onOpenChange, vendita, editing, onSaved, aziendaIvaDefault }) {
  const [providerId, setProviderId] = useState(null);
  const [descrizione, setDescrizione] = useState("");
  const [durata, setDurata] = useState(12);
  const [prezzoAcq, setPrezzoAcq] = useState(0);
  const [prezzoVen, setPrezzoVen] = useState(0);
  const [note, setNote] = useState("");
  // Sprint 4F1.1: provvigione esplicita
  const [provAttiva, setProvAttiva] = useState(false);
  const [provTipo, setProvTipo] = useState("percentuale");
  const [provPerc, setProvPerc] = useState("");
  const [provFisso, setProvFisso] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Sprint 4I1.B2.C — IVA acquisto/vendita garanzia
  // Sprint 4I1.B2.A.3 — default null: backend propaga da provider.iva_codice
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [ivaAcq, setIvaAcq] = useState(null);
  const [ivaVen, setIvaVen] = useState(null);
  // Sprint 4I1.C — tracking origine pre-fill IVA
  const [ivaAcqSource, setIvaAcqSource] = useState(null);
  const [ivaVenSource, setIvaVenSource] = useState(null);
  const [providerData, setProviderData] = useState(null); // {nome, iva_codice}
  const ivaOrigin = editing?.iva_codice_origin;
  const isOverride = !!editing?.iva_codice_override || ivaOrigin === "override_utente";
  const originBadge = (() => {
    if (!ivaOrigin) return null;
    if (ivaOrigin === "default_categoria")
      return { label: "Default", cls: "bg-amber-50 text-amber-700 border-amber-200" };
    if (ivaOrigin === "override_utente")
      return { label: "Override", cls: "bg-rose-50 text-rose-700 border-rose-200" };
    return null;
  })();

  const isNew = !editing?.id;

  useEffect(() => {
    if (!open) return;
    setProviderId(editing?.provider_id || null);
    setDescrizione(editing?.descrizione || "");
    setDurata(editing?.durata_mesi || 12);
    setPrezzoAcq(editing?.prezzo_acquisto || 0);
    setPrezzoVen(editing?.prezzo_vendita || 0);
    setNote(editing?.note || "");
    setProvAttiva(!!editing?.provvigione_attiva);
    setProvTipo(editing?.provvigione_tipo || "percentuale");
    setProvPerc(editing?.provvigione_percentuale ?? "");
    setProvFisso(editing?.provvigione_importo_fisso ?? "");
    // Sprint 4I1.B2.C / B2.A.3 — leggi valori reali dalla riga (no fallback hardcoded)
    setIvaAcq(editing?.iva_codice_acquisto || null);
    setIvaVen(editing?.iva_codice_vendita || null);
    setAdvancedOpen(false);
    // Sprint 4I1.C.1 — Per garanzie esistenti con override DB-side preserva
    // il source="override" così il save reinvia l'IVA esplicita.
    const hasDbOverride = !!(editing?.iva_codice_override
      || editing?.iva_codice_origin === "override_utente");
    setIvaAcqSource(hasDbOverride ? "override" : null);
    setIvaVenSource(hasDbOverride ? "override" : null);
    setProviderData(null);
  }, [open, editing]);

  // Sprint 4I1.C — Pre-fill IVA al mount per nuove garanzie (da azienda default)
  useEffect(() => {
    if (!open || !isNew || !aziendaIvaDefault) return;
    setIvaAcq((cur) => cur || aziendaIvaDefault);
    setIvaVen((cur) => cur || aziendaIvaDefault);
    setIvaAcqSource((s) => s || "azienda");
    setIvaVenSource((s) => s || "azienda");
  }, [open, isNew, aziendaIvaDefault]);

  // Sprint 4I1.C — Quando cambia provider, fetch anagrafica e propaga IVA
  // sui codici NON ancora override
  useEffect(() => {
    if (!open) return;
    if (!providerId) { setProviderData(null); return; }
    let cancelled = false;
    api.get(`/anagrafiche/${providerId}`).then((r) => {
      if (cancelled) return;
      const a = r.data || {};
      const ivaCod = a.iva_codice || null;
      const nameLabel = a.ragione_sociale
        || `${a.cognome || ""} ${a.nome || ""}`.trim()
        || a.codice || "Compagnia";
      setProviderData({ nome: nameLabel, iva_codice: ivaCod });
      if (ivaCod) {
        if (ivaAcqSource !== "override") {
          setIvaAcq(ivaCod);
          setIvaAcqSource("fornitore");
        }
        if (ivaVenSource !== "override") {
          setIvaVen(ivaCod);
          setIvaVenSource("fornitore");
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, providerId]);

  // Anteprime client-side
  const margineGaranzia = (Number(prezzoVen) || 0) - (Number(prezzoAcq) || 0);
  const provPercNum = Number(provPerc);
  const provFissoNum = Number(provFisso);
  const hasValidProvValue =
    provAttiva &&
    ((provTipo === "percentuale" && provPerc !== "" && !isNaN(provPercNum) && provPercNum >= 0 && provPercNum <= 100) ||
     (provTipo === "fisso" && provFisso !== "" && !isNaN(provFissoNum) && provFissoNum >= 0));
  const provvAnteprima = hasValidProvValue
    ? (provTipo === "percentuale"
       ? Math.round((Number(prezzoAcq) || 0) * (provPercNum / 100) * 100) / 100
       : Math.round(provFissoNum * 100) / 100)
    : 0;

  // Validazioni
  const canSubmit =
    !!providerId &&
    (descrizione || "").trim().length >= 3 &&
    DURATA_OPTS.includes(Number(durata)) &&
    Number(prezzoAcq) >= 0 && Number(prezzoVen) >= 0 &&
    (!provAttiva || hasValidProvValue);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Compila correttamente i campi obbligatori");
      return;
    }
    setSubmitting(true);
    const body = {
      provider_id: providerId,
      descrizione: descrizione.trim(),
      durata_mesi: Number(durata),
      prezzo_acquisto: Number(prezzoAcq) || 0,
      prezzo_vendita: Number(prezzoVen) || 0,
      provvigione_attiva: !!provAttiva,
      provvigione_tipo: provAttiva ? provTipo : null,
      provvigione_percentuale: provAttiva && provTipo === "percentuale" ? provPercNum : null,
      provvigione_importo_fisso: provAttiva && provTipo === "fisso" ? provFissoNum : null,
      note: (note || "").trim() || null,
    };
    // Sprint 4I1.C.1 — Invia IVA esplicita SOLO se source==="override".
    // Cambio provider = propagazione automatica (no override).
    if (ivaAcqSource === "override" && ivaAcq) {
      body.iva_codice_acquisto = ivaAcq;
    }
    if (ivaVenSource === "override" && ivaVen) {
      body.iva_codice_vendita = ivaVen;
    }
    try {
      if (editing?.id) {
        await api.patch(`/vendite/${vendita.id}/garanzie/${editing.id}`, body);
        toast.success("Garanzia aggiornata");
      } else {
        await api.post(`/vendite/${vendita.id}/garanzie`, body);
        toast.success("Garanzia aggiunta");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="garanzia-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className={`h-5 w-5 ${PROVV_TEXT}`} />
            {editing?.id ? "Modifica garanzia" : "Nuova garanzia"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* SEZIONE PROVIDER */}
          <div>
            <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-1">Provider</div>
            <AnagraficaPicker
              label=""
              value={providerId}
              onChange={setProviderId}
              filterCategoria="fornitore_servizi_veicoli.garanzia_accessoria"
              contextCompanyId={vendita?.company_id || null}
              testIdPrefix="picker-provider-garanzia"
            />
          </div>

          {/* SEZIONE DETTAGLI */}
          <div className="border-t pt-3">
            <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Dettagli garanzia</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Descrizione <span className="text-rose-600">*</span></Label>
                <Input
                  value={descrizione}
                  onChange={(e) => setDescrizione(e.target.value)}
                  placeholder="Es. Estensione meccanica 24 mesi"
                  data-testid="garanzia-descrizione"
                />
              </div>
              <div>
                <Label className="text-xs">Durata (mesi) <span className="text-rose-600">*</span></Label>
                <Select value={String(durata)} onValueChange={(v) => setDurata(Number(v))}>
                  <SelectTrigger data-testid="garanzia-durata">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATA_OPTS.map((d) => (
                      <SelectItem key={d} value={String(d)}>{d} mesi</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* SEZIONE IMPORTI */}
          <div className="border-t pt-3">
            <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Importi</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-amber-700">Prezzo acquisto <span className="text-rose-600">*</span></Label>
                <CurrencyInput
                  value={prezzoAcq}
                  onChange={(v) => setPrezzoAcq(v ?? 0)}
                  data-testid="garanzia-acquisto"
                  className="border-l-4 border-amber-300"
                />
              </div>
              <div>
                <Label className="text-xs text-blue-700">Prezzo vendita <span className="text-rose-600">*</span></Label>
                <CurrencyInput
                  value={prezzoVen}
                  onChange={(v) => setPrezzoVen(v ?? 0)}
                  data-testid="garanzia-vendita"
                  className="border-l-4 border-blue-300"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2"
                 data-testid="garanzia-margine-preview">
              Margine garanzia (vendita − acquisto):{" "}
              <span className={`font-mono font-semibold ${margineGaranzia < 0 ? "text-rose-600" : "text-slate-700"}`}>
                {formatEUR(margineGaranzia)}
              </span>
              {margineGaranzia < 0 && (
                <span className="text-rose-600 ml-2 text-[10px]">⚠️ sotto costo</span>
              )}
            </div>
          </div>

          {/* SEZIONE PROVVIGIONE PROVIDER */}
          <div className="border-t pt-3">
            <div className="flex items-center gap-3 mb-2">
              <Switch
                checked={provAttiva}
                onCheckedChange={setProvAttiva}
                data-testid="garanzia-provv-toggle"
              />
              <Label className="text-sm font-medium cursor-pointer"
                     onClick={() => setProvAttiva(!provAttiva)}>
                Garanzia con provvigione riconosciuta dal provider
              </Label>
            </div>
            <div className={`pl-2 space-y-3 ${provAttiva ? "" : "opacity-40 pointer-events-none"}`}
                 data-testid="garanzia-provv-block">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio"
                         checked={provTipo === "percentuale"}
                         onChange={() => setProvTipo("percentuale")}
                         disabled={!provAttiva}
                         data-testid="garanzia-provv-radio-perc" />
                  Percentuale (% sul prezzo acquisto)
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio"
                         checked={provTipo === "fisso"}
                         onChange={() => setProvTipo("fisso")}
                         disabled={!provAttiva}
                         data-testid="garanzia-provv-radio-fisso" />
                  Importo fisso (€)
                </label>
              </div>
              {provTipo === "percentuale" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">% sul prezzo acquisto</Label>
                    <div className="relative">
                      <Input
                        type="number" step="0.1" min="0" max="100"
                        value={provPerc}
                        onChange={(e) => setProvPerc(e.target.value)}
                        disabled={!provAttiva}
                        placeholder="0,0"
                        className="pr-7"
                        data-testid="garanzia-provv-percentuale"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">%</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Importo fisso</Label>
                    <CurrencyInput
                      value={provFisso}
                      onChange={(v) => setProvFisso(v ?? "")}
                      data-testid="garanzia-provv-fisso"
                    />
                  </div>
                </div>
              )}
              {hasValidProvValue && (
                <div className={`rounded-md px-3 py-2 text-sm border ${PROVV_BG} ${PROVV_BORDER}`}
                     data-testid="garanzia-provv-anteprima">
                  <span className="font-medium text-slate-600">Anteprima provvigione:</span>{" "}
                  <span className={`font-mono font-semibold ${PROVV_TEXT}`}>
                    {formatEUR(provvAnteprima)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* SEZIONE NOTE */}
          <div className="border-t pt-3">
            <Label className="text-[11px] uppercase tracking-wide font-medium text-slate-500">Note (opzionale)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      data-testid="garanzia-note" />
          </div>

          {/* Sprint 4I1.B2.C — SEZIONE AVANZATO (IVA) */}
          <div className="border-t pt-3">
            <button type="button"
                    onClick={() => setAdvancedOpen((x) => !x)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                    data-testid="garanzia-avanzato-toggle">
              <span>{advancedOpen ? "▼" : "▶"}</span>
              <span>Avanzato / Amministrativo</span>
              {originBadge && (
                <span className={`ml-2 px-2 py-0.5 rounded text-[10px] border ${originBadge.cls}`}
                      data-testid="garanzia-iva-origin-badge">
                  {originBadge.label}
                </span>
              )}
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-3 rounded-md bg-slate-50 border p-3"
                   data-testid="garanzia-avanzato-content">
                <div>
                  <Label className="text-xs">Codice IVA acquisto</Label>
                  <CodiceIvaPicker value={ivaAcq}
                                   onChange={(v) => { setIvaAcq(v); setIvaAcqSource("override"); }}
                                   testId="garanzia-iva-acquisto" placeholder="Selezionare..." />
                  <OrigineIvaLabel source={ivaAcqSource} fornitoreNome={providerData?.nome}
                                   testId="garanzia-iva-acq-origine" />
                </div>
                <div>
                  <Label className="text-xs">Codice IVA vendita</Label>
                  <CodiceIvaPicker value={ivaVen}
                                   onChange={(v) => { setIvaVen(v); setIvaVenSource("override"); }}
                                   testId="garanzia-iva-vendita" placeholder="Selezionare..." />
                  <OrigineIvaLabel source={ivaVenSource} fornitoreNome={providerData?.nome}
                                   testId="garanzia-iva-ven-origine" />
                </div>
                <p className="text-[11px] text-slate-500">
                  Default da fornitore selezionato (es. compagnia assicurativa <span className="font-mono">E10</span>,
                  società servizi <span className="font-mono">I22</span>). Modificabile per casi specifici.
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
                  data-testid="btn-save-garanzia">
            {submitting ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
